import { Trans, useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { AlertBanner, Modal } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

import {
    DEAL_SIGNAL_CSV_EXAMPLE_ROWS,
    DEAL_SIGNAL_CSV_HEADERS,
    DEAL_SIGNAL_MAX_COMPANIES,
    DEAL_SIGNAL_TEMPLATE_URL,
    DEAL_SIGNAL_UPLOAD_COOLDOWN_DAYS,
} from "../../constants";
import signalRecommenderI18nKeys from "../../utils/i18n";
import { DealSignalCsvInput } from "../DealSignalCsvInput/DealSignalCsvInput";

import {
    StyledExampleTable,
    StyledInstructionItem,
    StyledInstructionsColumn,
    StyledInstructionsList,
    StyledTemplateLink,
    StyledUploadLayout,
} from "./styles";
import type { DealSignalCsvValidationError } from "./types";
import { UploadValidationError } from "./UploadValidationError";

type UploadStepProps = {
    readonly isValidating: boolean;
    readonly validationError?: DealSignalCsvValidationError;
    readonly onFileSelect: (file: File) => void;
};

export const UploadStep = ({ isValidating, validationError, onFileSelect }: UploadStepProps) => {
    const { translate } = useTranslate();

    const maxCompaniesRule = signalRecommenderI18nKeys.upload.rule.maxCompanies({
        limit: formatNumberWithComa(DEAL_SIGNAL_MAX_COMPANIES),
    });

    const limitNotice = signalRecommenderI18nKeys.upload.limitNotice({
        days: String(DEAL_SIGNAL_UPLOAD_COOLDOWN_DAYS),
    });

    return (
        <>
            <Modal.Header withBorder>
                <Modal.Title data-automation="signal-recommender-upload-modal-title">
                    {translate(signalRecommenderI18nKeys.upload.title)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Content>
                <UploadValidationError error={validationError} />

                <StyledUploadLayout>
                    <StyledInstructionsColumn>
                        <StyledInstructionsList>
                            <StyledInstructionItem>
                                {translate(signalRecommenderI18nKeys.upload.rule.headerRow)}
                            </StyledInstructionItem>
                            <StyledInstructionItem>
                                {translate(signalRecommenderI18nKeys.upload.rule.requiredColumns)}
                            </StyledInstructionItem>
                            <StyledInstructionItem>
                                {translate(signalRecommenderI18nKeys.upload.rule.matchingRates)}
                            </StyledInstructionItem>
                            <StyledInstructionItem>
                                {translate(signalRecommenderI18nKeys.upload.rule.signalTimelines)}
                            </StyledInstructionItem>
                            <StyledInstructionItem>
                                {translate(maxCompaniesRule.key, maxCompaniesRule.replacements)}
                            </StyledInstructionItem>
                        </StyledInstructionsList>

                        <Box margin="16px 0 0">
                            <Text
                                fontVariant="button"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate(signalRecommenderI18nKeys.upload.requiredColumnsTitle)}
                            </Text>

                            <StyledExampleTable
                                quantity={DEAL_SIGNAL_CSV_HEADERS.length}
                                data-automation="signal-recommender-upload-example-table"
                            >
                                {DEAL_SIGNAL_CSV_HEADERS.map((header) => (
                                    <div
                                        key={header}
                                        className="header-cell"
                                    >
                                        {header}
                                    </div>
                                ))}

                                {DEAL_SIGNAL_CSV_EXAMPLE_ROWS.flatMap((row, rowIndex) =>
                                    row.map((cell, cellIndex) => <div key={`${rowIndex}-${cellIndex}`}>{cell}</div>),
                                )}
                            </StyledExampleTable>
                        </Box>
                    </StyledInstructionsColumn>

                    <Stack
                        flex="1 1 0"
                        minWidth="0"
                        gap="16px"
                        padding="0 0 0 24px"
                        justifyContent="center"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[400]}
                            >
                                <Trans
                                    i18nKey={signalRecommenderI18nKeys.upload.templateHelp}
                                    components={{
                                        a: (
                                            <StyledTemplateLink
                                                data-automation="signal-recommender-template-link"
                                                href={DEAL_SIGNAL_TEMPLATE_URL}
                                                download
                                            />
                                        ),
                                    }}
                                />
                            </Text>
                        </Flex>

                        <DealSignalCsvInput
                            isValidating={isValidating}
                            onFileSelect={onFileSelect}
                        />

                        <AlertBanner
                            variant="info"
                            data-automation="signal-recommender-upload-limit-notice"
                        >
                            <AlertBanner.Image />
                            <AlertBanner.Description>
                                {translate(limitNotice.key, limitNotice.replacements)}
                            </AlertBanner.Description>
                        </AlertBanner>
                    </Stack>
                </StyledUploadLayout>
            </Modal.Content>
        </>
    );
};
